import { useEffect, useMemo, useState } from 'react';
import {
  getSeedSources,
  startSeedImport,
  getSeedBatches,
  getSeedBatch,
  cancelSeedImport,
  countSeeds,
  manageSeeds,
  type SeedBatch,
  type SeedFilter,
  type SeedOp,
} from '../api';
import { useApi } from '../useApi';
import { fmtDate } from '../format';

/**
 * Seed importer — bring scraped datasets (uploaded to the server's IMPORT_DATA_DIR)
 * into FLM as marked, deletable seed users. Import runs in the background (progress
 * polled); deletion is hard-scoped to seed accounts (real users never touched).
 */

const statusClass = (s: SeedBatch['status']) =>
  s === 'done' ? 'badge badge-ok' : s === 'failed' ? 'badge badge-danger' : 'badge badge-warn';

function CountryChecks({ options, selected, onToggle }: { options: string[]; selected: string[]; onToggle: (c: string) => void }) {
  if (!options.length) return <span className="hint">No countries in this dataset.</span>;
  return (
    <div className="checks-row">
      {options.map((c) => (
        <label key={c} className="check">
          <input type="checkbox" checked={selected.includes(c)} onChange={() => onToggle(c)} /> {c}
        </label>
      ))}
    </div>
  );
}

export function Importer() {
  const { data, error, loading } = useApi(getSeedSources);
  const sources = useMemo(() => data?.sources ?? [], [data]);

  // ---- import form ----
  const [source, setSource] = useState('');
  useEffect(() => {
    if (!source && sources.length) setSource(sources[0].source);
  }, [source, sources]);
  const current = sources.find((s) => s.source === source);
  const countryOpts = current ? Object.keys(current.byCountry).sort() : [];

  const [countries, setCountries] = useState<string[]>([]);
  const [gender, setGender] = useState<'' | 'man' | 'woman'>('');
  const [minPhotos, setMinPhotos] = useState('1');
  const [limit, setLimit] = useState('1000');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [city, setCity] = useState('');
  const [starting, setStarting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [batches, setBatches] = useState<SeedBatch[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const loadBatches = () => getSeedBatches().then(setBatches).catch(() => {});
  useEffect(() => { loadBatches(); }, []);
  const cancel = (id: string) => {
    if (!window.confirm('Cancel this import? Already-imported users are kept.')) return;
    cancelSeedImport(id).then(loadBatches).catch(() => {});
  };

  // poll the running batch for the progress bar
  useEffect(() => {
    if (!activeId) return;
    let alive = true;
    const tick = () =>
      getSeedBatch(activeId)
        .then((b) => {
          if (!alive) return;
          setBatches((prev) => (prev.some((x) => x.id === b.id) ? prev.map((x) => (x.id === b.id ? b : x)) : [b, ...prev]));
          if (b.status === 'running') setTimeout(tick, 1500);
          else { setActiveId(null); loadBatches(); }
        })
        .catch(() => { if (alive) setActiveId(null); });
    tick();
    return () => { alive = false; };
  }, [activeId]);

  const toggleCountry = (c: string) => setCountries((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  const start = async () => {
    if (!source) return;
    setStarting(true);
    setActionError(null);
    try {
      const { batchId } = await startSeedImport({
        source,
        countries: countries.length ? countries : undefined,
        gender: gender || undefined,
        minPhotos: Math.max(0, Math.floor(Number(minPhotos) || 0)),
        limit: Math.max(1, Math.floor(Number(limit) || 1)),
        ageMin: ageMin.trim() ? Math.floor(Number(ageMin)) : undefined,
        ageMax: ageMax.trim() ? Math.floor(Number(ageMax)) : undefined,
        city: city.trim() || undefined,
      });
      setActiveId(batchId);
      loadBatches();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Import failed to start.');
    } finally {
      setStarting(false);
    }
  };

  const active = batches.find((b) => b.id === activeId) ?? null;
  const pct = active && active.requested ? Math.min(100, Math.round(((active.imported + active.skipped + active.failed) / active.requested) * 100)) : 0;

  // ---- manage section (count / deactivate / reactivate / delete) ----
  const [dSource, setDSource] = useState('');
  const [dCountries, setDCountries] = useState('');
  const [dCity, setDCity] = useState('');
  const [dGender, setDGender] = useState<'' | 'man' | 'woman'>('');
  const [dAgeMin, setDAgeMin] = useState('');
  const [dAgeMax, setDAgeMax] = useState('');
  const [dStatus, setDStatus] = useState<'' | 'active' | 'suspended'>('');
  const [dCount, setDCount] = useState<number | null>(null);
  const [dBusy, setDBusy] = useState(false);

  const dFilter = (): SeedFilter => ({
    source: dSource || undefined,
    countries: dCountries.trim() ? dCountries.split(',').map((c) => c.trim()).filter(Boolean) : undefined,
    city: dCity.trim() || undefined,
    gender: dGender || undefined,
    ageMin: dAgeMin.trim() ? Math.floor(Number(dAgeMin)) : undefined,
    ageMax: dAgeMax.trim() ? Math.floor(Number(dAgeMax)) : undefined,
    status: dStatus || undefined,
  });
  const doCount = async () => {
    setDBusy(true);
    setActionError(null);
    try {
      setDCount((await countSeeds(dFilter())).count);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Count failed.');
    } finally {
      setDBusy(false);
    }
  };
  const doManage = async (op: SeedOp) => {
    const f = dFilter();
    const scope = [f.source ? `source ${f.source}` : 'ALL sources', f.countries?.length ? f.countries.join('/') : 'all countries', f.city || '', f.gender || 'all genders'].filter(Boolean).join(' · ');
    const verb = op === 'delete' ? 'Delete' : op === 'deactivate' ? 'Deactivate (hide)' : 'Reactivate';
    if (!window.confirm(`${verb} seed users (${scope})? Real users are never affected.${op === 'delete' ? ' This cannot be undone.' : ''}`)) return;
    setDBusy(true);
    setActionError(null);
    try {
      const res = await manageSeeds(op, f);
      const n = res.deleted ?? res.affected ?? 0;
      setDCount(null);
      window.alert(`${op === 'delete' ? 'Deleted' : op === 'deactivate' ? 'Deactivated' : 'Reactivated'} ${n} seed users.`);
      loadBatches();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : `${verb} failed.`);
    } finally {
      setDBusy(false);
    }
  };

  return (
    <div className="admin-wrap">
      <div className="page-head">
        <h1>Importer<span className="dot">.</span></h1>
      </div>
      <p className="hint">
        Imports scraped datasets uploaded to <code>{data?.dir ?? 'IMPORT_DATA_DIR'}</code> as <strong>seed users</strong>
        {' '}(marked, never confused with real Telegram users). Layout: <code>&lt;source&gt;/profiles/&lt;COUNTRY&gt;/&lt;id&gt;/profile.json</code>.
      </p>
      {actionError && <p className="error-line">{actionError}</p>}

      {error ? (
        <div className="card"><div className="empty">{error}</div></div>
      ) : loading && !data ? (
        <div className="card"><div className="empty">Scanning datasets…</div></div>
      ) : sources.length === 0 ? (
        <div className="card"><div className="empty">No datasets found under {data?.dir}. Upload one and reload.</div></div>
      ) : (
        <>
          {/* ---- import ---- */}
          <section className="card">
            <div className="card-head"><span className="card-title">Import</span></div>
            <div className="form-grid">
              <div className="field">
                <label>Dataset</label>
                <select className="input" value={source} onChange={(e) => { setSource(e.target.value); setCountries([]); }}>
                  {sources.map((s) => <option key={s.source} value={s.source}>{s.source} ({s.total})</option>)}
                </select>
              </div>
              <div className="field">
                <label>Gender</label>
                <select className="input" value={gender} onChange={(e) => setGender(e.target.value as '' | 'man' | 'woman')}>
                  <option value="">Any</option>
                  <option value="woman">Women</option>
                  <option value="man">Men</option>
                </select>
              </div>
              <div className="field">
                <label>Min photos</label>
                <input className="input" value={minPhotos} onChange={(e) => setMinPhotos(e.target.value)} />
              </div>
              <div className="field">
                <label>Age min</label>
                <input className="input" placeholder="any" value={ageMin} onChange={(e) => setAgeMin(e.target.value)} />
              </div>
              <div className="field">
                <label>Age max</label>
                <input className="input" placeholder="any" value={ageMax} onChange={(e) => setAgeMax(e.target.value)} />
              </div>
              <div className="field">
                <label>City contains</label>
                <input className="input" placeholder="any" value={city} onChange={(e) => setCity(e.target.value)} />
              </div>
              <div className="field">
                <label>How many (limit)</label>
                <input className="input" value={limit} onChange={(e) => setLimit(e.target.value)} />
              </div>
            </div>
            <div className="field" style={{ marginTop: 14 }}>
              <label>Countries <span className="hint" style={{ textTransform: 'none', letterSpacing: 0 }}>— none = all</span></label>
              <CountryChecks options={countryOpts} selected={countries} onToggle={toggleCountry} />
            </div>
            <div style={{ marginTop: 16 }}>
              <button className="btn btn-primary" disabled={starting || !!active} onClick={() => void start()}>
                {active ? 'Importing…' : starting ? 'Starting…' : 'Start import'}
              </button>
            </div>
            {active && (
              <div style={{ marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: 6 }}>
                  <span className="muted">Importing {active.source}…</span>
                  <span className="mono">{active.imported} imported · {active.skipped} skipped · {active.failed} failed / {active.requested}</span>
                </div>
                <div className="split-track"><div className="split-fill" style={{ width: `${pct}%`, background: 'var(--accent)' }} /></div>
              </div>
            )}
          </section>

          {/* ---- manage (deactivate / reactivate / delete) ---- */}
          <section className="card">
            <div className="card-head"><span className="card-title">Manage seed users</span></div>
            <p className="hint" style={{ marginBottom: 12 }}>
              Only ever touches <strong>seed</strong> accounts — real users are structurally excluded. Leave a filter blank to
              ignore it. <strong>Deactivate</strong> hides them from the app (reversible); <strong>Delete</strong> removes them for good.
            </p>
            <div className="form-grid">
              <div className="field">
                <label>Source</label>
                <select className="input" value={dSource} onChange={(e) => { setDSource(e.target.value); setDCount(null); }}>
                  <option value="">All sources</option>
                  {sources.map((s) => <option key={s.source} value={s.source}>{s.source}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Countries (comma)</label>
                <input className="input" placeholder="e.g. US,PH" value={dCountries} onChange={(e) => { setDCountries(e.target.value); setDCount(null); }} />
              </div>
              <div className="field">
                <label>City contains</label>
                <input className="input" placeholder="any" value={dCity} onChange={(e) => { setDCity(e.target.value); setDCount(null); }} />
              </div>
              <div className="field">
                <label>Gender</label>
                <select className="input" value={dGender} onChange={(e) => { setDGender(e.target.value as '' | 'man' | 'woman'); setDCount(null); }}>
                  <option value="">Any</option>
                  <option value="woman">Women</option>
                  <option value="man">Men</option>
                </select>
              </div>
              <div className="field">
                <label>Age min</label>
                <input className="input" placeholder="any" value={dAgeMin} onChange={(e) => { setDAgeMin(e.target.value); setDCount(null); }} />
              </div>
              <div className="field">
                <label>Age max</label>
                <input className="input" placeholder="any" value={dAgeMax} onChange={(e) => { setDAgeMax(e.target.value); setDCount(null); }} />
              </div>
              <div className="field">
                <label>Status</label>
                <select className="input" value={dStatus} onChange={(e) => { setDStatus(e.target.value as '' | 'active' | 'suspended'); setDCount(null); }}>
                  <option value="">Any</option>
                  <option value="active">Active</option>
                  <option value="suspended">Deactivated</option>
                </select>
              </div>
            </div>
            <div className="row-actions" style={{ marginTop: 14, alignItems: 'center' }}>
              <button className="btn" disabled={dBusy} onClick={() => void doCount()}>{dBusy ? '…' : 'Count matching'}</button>
              {dCount != null && <span className="muted">{dCount} seed users match</span>}
              <span style={{ flex: 1 }} />
              <button className="btn" disabled={dBusy} onClick={() => void doManage('deactivate')}>Deactivate</button>
              <button className="btn" disabled={dBusy} onClick={() => void doManage('activate')}>Reactivate</button>
              <button className="btn btn-danger" disabled={dBusy} onClick={() => void doManage('delete')}>Delete</button>
            </div>
          </section>

          {/* ---- history ---- */}
          <div className="section-head"><h2>Recent imports<span className="dot">.</span></h2></div>
          <div className="table-wrap">
            {batches.length === 0 ? (
              <div className="empty">No imports yet.</div>
            ) : (
              <table className="flm">
                <thead><tr><th>When</th><th>Source</th><th>Status</th><th>Imported</th><th>Skipped</th><th>Failed</th><th>Requested</th><th></th></tr></thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id}>
                      <td className="muted">{fmtDate(b.createdAt)}</td>
                      <td>{b.source}</td>
                      <td><span className={statusClass(b.status)}>{b.status}</span></td>
                      <td>{b.imported}</td>
                      <td className="muted">{b.skipped}</td>
                      <td className="muted">{b.failed}</td>
                      <td className="muted">{b.requested}</td>
                      <td>{b.status === 'running' ? <button className="btn btn-danger" onClick={() => cancel(b.id)}>Cancel</button> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
